import axios from 'axios';
import { getApiBaseUrl } from '../config/api';

export interface TreatmentRecord {
  id: string;
  birdId: string;
  bird?: {
    bandId: string;
    species: { name: string };
  };
  healthTestId?: string;
  healthTest?: any;
  treatmentName: string;
  treatmentType: 'MEDICATION' | 'INJECTION' | 'TOPICAL' | 'ORAL' | 'SURGERY' | 'THERAPY' | 'SUPPLEMENT';
  treatmentStatus: 'PRESCRIBED' | 'IN_PROGRESS' | 'COMPLETED' | 'DISCONTINUED' | 'ON_HOLD';
  medicationName?: string;
  activeIngredient?: string;
  dosage: string;
  frequency: 'ONCE_DAILY' | 'TWICE_DAILY' | 'THREE_TIMES_DAILY' | 'EVERY_6_HOURS' | 'EVERY_8_HOURS' | 'EVERY_12_HOURS' | 'WEEKLY' | 'AS_NEEDED' | 'SINGLE_DOSE';
  administrationRoute: string;
  startDate: string;
  endDate?: string;
  prescribedBy: string;
  veterinarian?: string;
  clinicName?: string;
  indication: string;
  instructions?: string;
  sideEffects?: string;
  contraindications?: string;
  monitoringNotes?: string;
  cost?: number;
  nextAdministration?: string;
  completedDoses: number;
  totalDoses?: number;
  effectivenessRating?: number;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTreatmentRecordDto {
  birdId: string;
  healthTestId?: string;
  treatmentName: string;
  treatmentType: 'MEDICATION' | 'INJECTION' | 'TOPICAL' | 'ORAL' | 'SURGERY' | 'THERAPY' | 'SUPPLEMENT';
  medicationName?: string;
  activeIngredient?: string;
  dosage: string;
  frequency: 'ONCE_DAILY' | 'TWICE_DAILY' | 'THREE_TIMES_DAILY' | 'EVERY_6_HOURS' | 'EVERY_8_HOURS' | 'EVERY_12_HOURS' | 'WEEKLY' | 'AS_NEEDED' | 'SINGLE_DOSE';
  administrationRoute: string;
  startDate: string;
  endDate?: string;
  prescribedBy: string;
  veterinarian?: string;
  clinicName?: string;
  indication: string;
  instructions?: string;
  sideEffects?: string;
  contraindications?: string;
  totalDoses?: number;
  cost?: number;
  notes?: string;
}

export interface UpdateTreatmentRecordDto {
  treatmentStatus?: 'PRESCRIBED' | 'IN_PROGRESS' | 'COMPLETED' | 'DISCONTINUED' | 'ON_HOLD';
  endDate?: string;
  completedDoses?: number;
  effectivenessRating?: number;
  monitoringNotes?: string;
  sideEffects?: string;
  notes?: string;
}

export interface TreatmentSummary {
  totalActive: number;
  totalCompleted: number;
  totalDiscontinued: number;
  activeTreatments: TreatmentRecord[];
  upcomingAdministrations: TreatmentRecord[];
  overdueAdministrations: TreatmentRecord[];
}

export interface MedicationAdministrationLog {
  treatmentId: string;
  administrationDate: string;
  dosageGiven: string;
  administeredBy: string;
  notes?: string;
  sideEffectsObserved?: string;
}

class TreatmentApi {
  private getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      Authorization: token ? `Bearer ${token}` : '',
      'Content-Type': 'application/json',
    };
  }

  async getAll(birdId?: string, status?: string): Promise<TreatmentRecord[]> {
    const response = await axios.get(`${getApiBaseUrl()}/health/treatments`, {
      headers: this.getAuthHeaders(),
      params: { birdId, status },
    });
    return response.data;
  }

  async getById(id: string): Promise<TreatmentRecord> {
    const response = await axios.get(`${getApiBaseUrl()}/health/treatments/${id}`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async create(data: CreateTreatmentRecordDto): Promise<TreatmentRecord> {
    const response = await axios.post(`${getApiBaseUrl()}/health/treatments`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async update(id: string, data: UpdateTreatmentRecordDto): Promise<TreatmentRecord> {
    const response = await axios.put(`${getApiBaseUrl()}/health/treatments/${id}`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async delete(id: string): Promise<void> {
    await axios.delete(`${getApiBaseUrl()}/health/treatments/${id}`, {
      headers: this.getAuthHeaders(),
    });
  }

  async getActive(birdId?: string): Promise<TreatmentRecord[]> {
    const response = await axios.get(`${getApiBaseUrl()}/health/treatments/active`, {
      headers: this.getAuthHeaders(),
      params: birdId ? { birdId } : {},
    });
    return response.data;
  }

  async getUpcoming(hours: number = 24): Promise<TreatmentRecord[]> {
    const response = await axios.get(`${getApiBaseUrl()}/health/treatments/upcoming`, {
      headers: this.getAuthHeaders(),
      params: { hours },
    });
    return response.data;
  }

  async getOverdue(): Promise<TreatmentRecord[]> {
    const response = await axios.get(`${getApiBaseUrl()}/health/treatments/overdue`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getSummary(): Promise<TreatmentSummary> {
    const response = await axios.get(`${getApiBaseUrl()}/health/treatments/summary`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getBirdTreatmentHistory(birdId: string): Promise<TreatmentRecord[]> {
    const response = await axios.get(`${getApiBaseUrl()}/health/treatments/bird/${birdId}/history`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async getEffectivenessReport(): Promise<any> {
    const response = await axios.get(`${getApiBaseUrl()}/health/treatments/effectiveness-report`, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async recordAdministration(id: string, log: Omit<MedicationAdministrationLog, 'treatmentId'>): Promise<TreatmentRecord> {
    const response = await axios.post(`${getApiBaseUrl()}/health/treatments/${id}/administer`, log, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async complete(id: string, data: {
    effectivenessRating?: number;
    notes?: string;
  }): Promise<TreatmentRecord> {
    const response = await axios.post(`${getApiBaseUrl()}/health/treatments/${id}/complete`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async discontinue(id: string, data: {
    reason?: string;
    notes?: string;
  }): Promise<TreatmentRecord> {
    const response = await axios.post(`${getApiBaseUrl()}/health/treatments/${id}/discontinue`, data, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async holdTreatment(id: string, reason?: string): Promise<TreatmentRecord> {
    const response = await axios.put(`${getApiBaseUrl()}/health/treatments/${id}`, {
      treatmentStatus: 'ON_HOLD',
      notes: reason,
    }, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }

  async resumeTreatment(id: string): Promise<TreatmentRecord> {
    const response = await axios.put(`${getApiBaseUrl()}/health/treatments/${id}`, {
      treatmentStatus: 'IN_PROGRESS',
    }, {
      headers: this.getAuthHeaders(),
    });
    return response.data;
  }
}

export const treatmentApi = new TreatmentApi(); 